/**
 * Onboarding Modal Component
 * First-time user tutorial
 */

import { useState } from "react";
import { X, Sparkles, FileText, Heart, ArrowRight, Check } from "lucide-react";
import { xhrPost, API } from "../utils/api";

const STEPS = [
  {
    icon: Sparkles,
    title: "Welcome to ChariAI!",
    description: "Create scroll-stopping video scripts for TikTok, Instagram Reels, and YouTube Shorts in seconds.",
    image: "🎬",
    tip: "Each platform has its own style and duration optimized for maximum engagement."
  },
  {
    icon: FileText,
    title: "Generate Your Script",
    description: "Enter your video idea, select a tone and category, then hit Generate. We'll create a complete production script with camera angles, lighting, and audio cues.",
    image: "✨",
    tip: "Try 'Surprise Me' to auto-generate trending ideas!"
  },
  {
    icon: Heart,
    title: "Save & Iterate",
    description: "Love a script? Save it to favorites. Want variations? Generate new versions with one click. Your history is always saved.",
    image: "💾",
    tip: "Upgrade to Pro for unlimited variations and favorites!"
  }
];

export function OnboardingModal({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      await xhrPost(`${API}/user/complete-onboarding`, {});
    } catch (err) {
      console.error("Failed to save onboarding status:", err);
    }
    setIsCompleting(false);
    onComplete();
    onClose();
  };

  const handleSkip = async () => {
    try {
      await xhrPost(`${API}/user/complete-onboarding`, {});
    } catch (err) {
      console.error("Failed to save onboarding status:", err);
    }
    onClose();
  };

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            {STEPS.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? "w-6 bg-violet-500"
                    : index < currentStep
                    ? "bg-violet-500"
                    : "bg-zinc-700"
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleSkip}
            className="text-zinc-500 hover:text-white transition-colors text-sm"
          >
            Skip
          </button>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          <div className="text-6xl mb-6">{step.image}</div>
          
          <div className="flex items-center justify-center gap-2 mb-3">
            <step.icon className="w-5 h-5 text-violet-400" />
            <h2 className="text-xl font-bold text-white">{step.title}</h2>
          </div>
          
          <p className="text-zinc-400 mb-6 leading-relaxed">
            {step.description}
          </p>

          <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
            <p className="text-sm text-violet-300">
              💡 <strong>Pro tip:</strong> {step.tip}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl transition-colors"
            >
              Back
            </button>
          )}
          
          {isLastStep ? (
            <button
              onClick={handleComplete}
              disabled={isCompleting}
              className="flex-1 py-3 bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {isCompleting ? (
                "Starting..."
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Get Started
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex-1 py-3 bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingModal;
